import { CdkTableModule } from '@angular/cdk/table';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, type OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgxFloatUiModule, NgxFloatUiPlacements, NgxFloatUiTriggers } from 'ngx-float-ui';
import { EMPTY, map, type Observable, Subject, switchMap } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';

import { hasApplicationRole, sessionUser$ } from '@auth/repository/auth.repository';
import type { IEntityInfo, IEntityLookupBean } from '@core/models/app.model';
import type { ICounterpartyListAction } from '@core/models/entity.model';
import type { IUserSession } from '@core/models/user.model';
import {
  type ISubAccount,
  SELECTED_FOR_ASSESSMENT,
  type SelectedForAssessmentType,
  SUB_ACCOUNT_ACTIONS,
} from '@core/models/virtual-premium.model';
import { NotifyService } from '@core/services/notifyService.service';
import { EDIT_DOCUMENT_ICON } from '@core/services/svg-service/svg-icons.helpers';
import { SvgIconService } from '@core/services/svg-service/svg-icons.service';
import { UmeCompanyLinkComponent } from '@shared/components/company-link/company-link.component';
import {
  APP_ROLE,
  COMPANY_ACTION,
  type CompanyAction,
  ONBOARDING_OPTIONS,
  type OnboardingOptionsAccess,
  OVERSIGHT_STATUS,
} from '@shared/constants/ume-constants';
import { ConfirmationDialogService } from '@shared/dialogs/confirmation-dialog/confirmation-dialog.service';
import { VpEnableAssessmentDialogComponent } from '@shared/dialogs/vp-enable-assessment-dialog/vp-enable-assessment-dialog.component';
import { OversightSubStatusComponent } from '@shared/oversight-status/oversight-sub-status/oversight-sub-status.component';
import { UmeCompanyService } from '@shared/services/ume-company.service';
import { VirtualPremiumService } from '@shared/services/virtual-premium.service';
import { SelectForAssessmentComponent } from '@widgets/select-for-assessment/select-for-assessment.component';
import { UmeFlagComponent } from '@widgets/ume-flag/ume-flag.component';

import { ActionHandlerService } from '../../../../../counterparties/services/action-handler.service';
import { ActionHelperService } from '../../../../../counterparties/services/action-helper.service';
import { SearchState } from '../../../../../search/state/search.state';
import { MY_ACCOUNT } from '../../../services-routes';

@UntilDestroy()
@Component({
  selector: 'ume-vp-accounts',
  templateUrl: './vp-accounts.component.html',
  styleUrls: ['./vp-accounts.component.scss'],
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDividerModule,
    CdkTableModule,
    OversightSubStatusComponent,
    UmeCompanyLinkComponent,
    MatMenuModule,
    SelectForAssessmentComponent,
    NgxFloatUiModule,
    MatSlideToggle,
    UmeFlagComponent,
  ],
  providers: [ActionHandlerService, ActionHelperService],
})
export class VPAccountsComponent implements OnInit {
  protected readonly popTriggers = NgxFloatUiTriggers;
  protected readonly popPlacements = NgxFloatUiPlacements;
  protected readonly oversightStatus = OVERSIGHT_STATUS;
  readonly selectAssessmentOption = SELECTED_FOR_ASSESSMENT;

  private confirmationService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private vpService: VirtualPremiumService = inject(VirtualPremiumService);
  private actionHandlerService = inject(ActionHandlerService);
  private umeCompanyService: UmeCompanyService = inject(UmeCompanyService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private dialog: MatDialog = inject(MatDialog);
  private notify: NotifyService = inject(NotifyService);
  private svgService = inject(SvgIconService);
  private readonly _searchState = inject(SearchState);

  public dataSource!: MatTableDataSource<ISubAccount>;
  public displayedColumns: string[] = [
    'name',
    'address',
    'status',
    'manageOptions',
    'selectedForAssessment',
    'actions',
  ];
  public currentCompany!: IEntityLookupBean;

  private readonly saBrId$ = new Subject<string | null>();
  public details$: Observable<Record<string, ISubAccount[]>> = this.saBrId$.pipe(
    filter(Boolean),
    switchMap((brId: string) =>
      this.vpService.getSubAccountsLookup({
        brId,
        companyActionName: '' as CompanyAction,
        source: SUB_ACCOUNT_ACTIONS.ENABLE_ASSESSMENT,
      })
    ),
    map((r: ISubAccount[]) => {
      return r.reduce(
        (acc, item: ISubAccount) => {
          const k = item.isEnabledForAssessment ? 'ENABLED' : 'DISABLED';
          if (!acc[k]) {
            acc[k] = [];
          }

          acc[k].push(item);
          return acc;
        },
        {} as Record<string, ISubAccount[]>
      );
    })
  );

  constructor() {
    this.svgService.addSvgIcon('edit_document', EDIT_DOCUMENT_ICON);
  }

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this)).subscribe((data) => {
      this.dataSource = new MatTableDataSource<ISubAccount>(data.subAccounts);
      this.cdr.markForCheck();
    });

    sessionUser$.pipe(untilDestroyed(this)).subscribe((user: IUserSession) => {
      if (user.selectedCompany) {
        this.currentCompany = user.selectedCompany;
      }
    });
  }

  get hasInviteRole(): boolean {
    return hasApplicationRole(APP_ROLE.INVITE_COUNTERPARTIES);
  }

  public isAllSaEnabledForAssessments(item: ISubAccount): boolean {
    return item.nroEnabledForAssessmentSA === item.subAccountsCount;
  }

  public getEnableTooltip(item: ISubAccount): string {
    return this.isAllSaEnabledForAssessments(item)
      ? 'All sub-accounts are enabled for assessment.'
      : 'Enable assessment';
  }

  public isEnableAssessmentBtnDisabled(item: ISubAccount): boolean {
    const isStatusNotShared = item.brStatus !== OVERSIGHT_STATUS.DDQ_SHARED;

    const alreadyAllEnabled = this.isAllSaEnabledForAssessments(item);

    return isStatusNotShared || alreadyAllEnabled;
  }

  public inviteSubAccount(): void {
    this._searchState.update({
      options: { [ONBOARDING_OPTIONS.onboardSubAccount]: null } as OnboardingOptionsAccess,
      formValue: null,
      onboardingData: null,
      onboardingBr: null,
      isVirtualPremium: true,
      avoidFirstStep: true,
    });

    this.router.navigate(['/', 'search', 'invite-sub-account']).then();
  }

  public goToEditDetails(): void {
    this.router.navigate(['/', MY_ACCOUNT, 'company-details']).then();
  }

  public enableForAssessment(subAccount: ISubAccount): void {
    if (subAccount.brGuid) {
      const br: string = subAccount.brGuid;
      let nroSelectedForAssessments = subAccount.nroEnabledForAssessmentSA;
      this.dialog
        .open(VpEnableAssessmentDialogComponent, {
          data: {
            brId: br,
            action: SUB_ACCOUNT_ACTIONS.ENABLE_ASSESSMENT,
            companyName: subAccount.companyInfo.companyName,
            globalLogicalStatus: subAccount.assessmentInfo?.selectedForAssessment,
            subAccountCount: subAccount.subAccountsCount,
            isDiscardEnabled: false,
          },
          width: '700px',
        })
        .afterClosed()
        .pipe(
          filter(Boolean),
          tap((selected: string[]) => {
            nroSelectedForAssessments += selected?.length ?? 0;
          }),
          switchMap((subAccountsIds: string[]) => {
            return this.vpService.enableVPAssessment(br, subAccountsIds).pipe(
              catchError(() => {
                this.notify.notifyError('Failed to enable assessment');
                return EMPTY;
              })
            );
          })
        )
        .subscribe(() => {
          this.replaceSelectedForAssessment(
            subAccount,
            SELECTED_FOR_ASSESSMENT.SELECTED,
            nroSelectedForAssessments
          );
          this.notify.notifySuccess('Assessment enabled');
        });
    }
  }

  public selectedForAssessmentChanged(
    logicalStatus: SelectedForAssessmentType,
    item: ISubAccount
  ): void {
    const br: string = item.brGuid;
    let hasArchiveOption: ICounterpartyListAction | undefined;

    if (logicalStatus === SELECTED_FOR_ASSESSMENT.DISCARDED) {
      this.vpService
        .getActionsForVP(br)
        .pipe(
          switchMap((actions) => {
            hasArchiveOption = actions.find((a) => a.action === COMPANY_ACTION.archive);
            return this.actionHandlerService.unselectedForAssessmentOrArchived({
              hasArchiveOption: Boolean(hasArchiveOption),
              isVirtualPremiumClient: true,
              action: hasArchiveOption ?? ({} as ICounterpartyListAction),
              company: item.companyInfo as unknown as IEntityInfo,
              subAccountsConfig: {
                brId: br,
                companyActionName: COMPANY_ACTION.archive,
                action: SUB_ACCOUNT_ACTIONS.ACTION,
                title: 'Select sub-accounts',
                question: `<p class="text">
                        Are you sure that you want to archive the counterparty to selected sub-accounts due diligence information?
                      </p>`,
                description: '',
                isSingleSelection: false,
              },
              vpAction: (_reason: string | undefined, guids: string[] | undefined) =>
                this.vpService.archiveRequestForVP(br, guids),
              defaultAction: (_reason: string | undefined) =>
                this.umeCompanyService.postCompanyLogicalStatus(
                  br,
                  SELECTED_FOR_ASSESSMENT.DISCARDED
                ),
            });
          }),
          untilDestroyed(this)
        )
        .subscribe(() => {
          this.refreshTableData(logicalStatus, item);
        });

      this.confirmationService
        .confirm({
          title: `Unselect ${item.companyInfo.companyName} for assessment`,
          text: `
            <p class="text">
            This will block your accessibility of the counterparty details, including all information 
            shared by the counterparty and your created and option to create assessments.
            </p>
            <p class="text"> Do you want to continue? </p>
          `,
          approveButton: { label: 'Continue' },
        })
        .pipe(
          filter(Boolean),
          switchMap(() => this.umeCompanyService.postCompanyLogicalStatus(br, logicalStatus)),
          untilDestroyed(this)
        )
        .subscribe(() => {
          this.replaceSelectedForAssessment(item, logicalStatus);
          this.getMessageBySelectedStatus(logicalStatus);
          this.cdr.markForCheck();
        });
    } else {
      this.umeCompanyService
        .postCompanyLogicalStatus(br, logicalStatus)
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.replaceSelectedForAssessment(item, logicalStatus);
          this.getMessageBySelectedStatus(logicalStatus);
          this.cdr.markForCheck();
        });
    }
  }

  public showPopover(brId: string): void {
    this.saBrId$.next(brId);
  }

  public hidePopover(): void {
    this.saBrId$.next(null);
  }

  public toggleManageOptions(brGuid: string, checked: boolean): void {
    this.vpService
      .manageOptionsForSA(brGuid, checked)
      .pipe(
        catchError(() => {
          const index = this.dataSource.data.findIndex((i: ISubAccount) => i.brGuid === brGuid);
          if (index !== -1) {
            this.dataSource.data[index].counterpartyManagementEnabled = !checked;
            this.dataSource.data = [...this.dataSource.data];
            this.cdr.markForCheck();
          }
          this.notify.notifyError(
            `Unable to ${checked ? 'enable' : 'disable'} manage options for sub-account. Please try again.`
          );
          return EMPTY;
        }),
        untilDestroyed(this)
      )
      .subscribe(() => {
        const index = this.dataSource.data.findIndex((i: ISubAccount) => i.brGuid === brGuid);
        if (index !== -1) {
          this.dataSource.data[index].counterpartyManagementEnabled = checked;
          this.dataSource.data = [...this.dataSource.data];
          this.cdr.markForCheck();
        }
        this.notify.notifySuccess(
          `Manage options for sub-account have been ${checked ? 'enabled' : 'disabled'}`
        );
      });
  }

  private getMessageBySelectedStatus(ls: SelectedForAssessmentType): void {
    switch (ls) {
      case SELECTED_FOR_ASSESSMENT.SELECTED:
        this.notify.notifySuccess('Counterparty has been selected.');
        break;
      case SELECTED_FOR_ASSESSMENT.NEW:
        this.notify.notifySuccess('Selection has been cleared');
        break;
      case SELECTED_FOR_ASSESSMENT.DISCARDED:
        this.notify.notifySuccess('Counterparty has been unselected.');
        break;
      default:
        return;
    }
  }

  private replaceSelectedForAssessment(
    item: ISubAccount,
    selectedForAssessment: SelectedForAssessmentType,
    nroSelectedForAssessment?: number
  ): void {
    const br: string | undefined = item.brGuid;
    if (!br) {
      return;
    }

    const index = this.dataSource.data.findIndex((i: ISubAccount) => i.brGuid === br);

    if (index !== -1) {
      this.dataSource.data[index] = {
        ...this.dataSource.data[index],
        nroEnabledForAssessmentSA: nroSelectedForAssessment
          ? nroSelectedForAssessment
          : this.dataSource.data[index].nroEnabledForAssessmentSA,
        assessmentInfo: {
          hasCurrentAssessment:
            this.dataSource.data[index].assessmentInfo?.hasCurrentAssessment || false,
          nroSelectedForAssessment:
            selectedForAssessment === SELECTED_FOR_ASSESSMENT.SELECTED
              ? this.dataSource.data[index].assessmentInfo?.nroSelectedForAssessment || 0
              : 0,
          selectedForAssessment,
        },
      };

      this.dataSource.data = [...this.dataSource.data];
    }

    this.cdr.markForCheck();
  }

  private refreshTableData(logicalStatus: SelectedForAssessmentType, row: ISubAccount): void {
    this.replaceSelectedForAssessment(row, logicalStatus);
    this.getMessageBySelectedStatus(logicalStatus);
    this.cdr.markForCheck();
  }
}
